import type { LeaderboardEntry } from '../types/leaderboard.types';
import { getSlateScopeLabels, UserRankDto } from '@sportspredict/shared';
import type { ProbabilityUser } from '../types/probability-event.types';
import {
	getOrgScopeLabel,
	type LeaderboardOrgScope,
} from '../components/leaderboard/leaderboard-org-scope-filter';
import type { LeaderboardBoardMode } from '../components/leaderboard/leaderboard-type-filter';

export const generateEmptyLeaderboardEntries = (count = 10): LeaderboardEntry[] => {
	return Array.from({ length: count }, (_, index) => ({
		rank: index + 1,
		userId: '',
		fullName: '-',
		score: '-',
	}));
};

export function getInitials(name?: string | null) {
	const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
	const initials =
		parts.length >= 2
			? `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`
			: (parts[0] ?? '').replace(/[^a-z0-9]/gi, '').slice(0, 2);

	return (initials || 'SP')
		.padEnd(2, initials[0] || 'P')
		.slice(0, 2)
		.toUpperCase();
}

export function isLegacyDefaultAvatar(src?: string | null): boolean {
	if (!src) return false;

	const normalizedSrc = src.toLowerCase();

	return (
		normalizedSrc.endsWith('.svg') ||
		normalizedSrc.includes('dicebear') ||
		normalizedSrc.includes('ui-avatars') ||
		normalizedSrc.includes('gravatar.com/avatar') ||
		normalizedSrc.includes('default-avatar') ||
		normalizedSrc.includes('avatar-default') ||
		normalizedSrc.includes('placeholder')
	);
}

export function normalizeImageVariants(value: unknown): { s?: string; xs?: string } | null {
	if (!value) return null;
	if (typeof value === 'object') return value as { s?: string; xs?: string };
	if (typeof value !== 'string') return null;

	try {
		const parsed = JSON.parse(value);
		if (parsed && typeof parsed === 'object') return parsed;
	} catch {
		return { s: value };
	}

	return null;
}

export function getRankClass(rank: number) {
	if (rank === 1) return 'text-[#D6A51F]';
	if (rank === 2) return 'text-[#848484]';
	if (rank === 3) return 'text-[#AD851F]';
	return 'text-[#474747]';
}

export function formatScore(score: number | string | undefined) {
	if (typeof score !== 'number') return '';
	return score.toFixed(2);
}

export function formatRbpScore(score?: number) {
	if (score == null || Number.isNaN(Number(score))) return '-';
	const value = Number(score);
	return value.toLocaleString('en-US', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

export function getCurrentUserLeaderboardEntry({
	currentUser,
	entries,
	userRank,
}: {
	currentUser?: ProbabilityUser;
	entries: LeaderboardEntry[];
	userRank?: UserRankDto;
}): LeaderboardEntry | null {
	if (!currentUser?.id || userRank?.rank == null || userRank?.score == null) return null;
	const isAlreadyVisible = entries.some((entry) => entry.userId === currentUser.id);
	if (isAlreadyVisible) return null;

	return {
		rank: Number(userRank.rank),
		userId: currentUser.id,
		fullName: currentUser.full_name || 'You',
		score: Number(userRank.score),
		imgVariants: normalizeImageVariants(currentUser.img_variants),
		country: currentUser.country,
	};
}

export function getLeaderboardPageTitle(args: {
	selectedRoundGroup?: string | null;
	selectedSlate?: string | null;
	categoryId?: string | null;
	boardMode: LeaderboardBoardMode;
	orgScope: LeaderboardOrgScope;
}): string {
	const { selectedRoundGroup, selectedSlate, boardMode, orgScope, categoryId } = args;
	if (selectedSlate) {
		return getSlateScopeLabels(categoryId).leaderboardTitle ?? `${selectedSlate} Leaderboard`;
	}
	if (selectedRoundGroup) return selectedRoundGroup;
	if (boardMode === 'match') return 'Match leaderboard';
	if (boardMode === 'knockout') return 'Knockout Stage';
	if (boardMode === 'finals') return 'Finals leaderboard';
	return `${getOrgScopeLabel(orgScope)} leaderboard`;
}
